import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useCreditEstimation } from '../../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../../hooks/useModeRestrictions';
import { generateDesign, uploadClothImage } from '../aiDesignerApi';
import {
  attributesToPayload,
  countDesignCombinations,
  GenderSetting,
} from '../config/clothTypes';
import { aiDesignerExamplePresets, AIDesignerExamplePreset } from '../examplePresets';

export interface AIDesignerSettings {
  gender: GenderSetting;
  clothType: string;
  designTheme: string;
  colorPalette: string;
  style: string;
  aspectRatio: string;
  variations: number;
}

export interface AIDesignerInitialState {
  inputImages?: string[];
  style?: string;
  variations?: number;
  generatedImages?: string[];
}

export type GenerateOutcome =
  | 'success'
  | 'no-image'
  | 'not-authenticated'
  | 'insufficient-credits'
  | 'error';

const DEFAULT_SETTINGS: AIDesignerSettings = {
  gender: 'female',
  clothType: 'auto',
  designTheme: 'automatic',
  colorPalette: 'auto',
  style: 'modern',
  aspectRatio: '9:16',
  variations: 1,
};

const buildInitialSettings = (initial?: AIDesignerInitialState): AIDesignerSettings => ({
  ...DEFAULT_SETTINGS,
  style: initial?.style || DEFAULT_SETTINGS.style,
  variations: initial?.variations || DEFAULT_SETTINGS.variations,
});

export const useAIDesigner = (initial?: AIDesignerInitialState) => {
  const { currentUser, userCredits, refreshUserCredits } = useAuth();
  const modeRestrictions = useModeRestrictions();

  const [clothImage, setClothImage] = useState<string>(
    (initial?.inputImages || []).filter(Boolean)[0] || ''
  );
  const [settings, setSettings] = useState<AIDesignerSettings>(() => buildInitialSettings(initial));
  const [attributes, setAttributes] = useState<Record<string, string[]>>({});
  const [mode, setMode] = useState<string>('performance');

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [generatedImages, setGeneratedImages] = useState<string[]>(initial?.generatedImages || []);
  const [isExample, setIsExample] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [generationTime, setGenerationTime] = useState(0);
  const [error, setError] = useState('');

  const [creditEstimation, estimateCreditCost] = useCreditEstimation('ai-designer');

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const generationAbortRef = useRef<AbortController | null>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);

  const hasPrimaryImage = Boolean(clothImage);
  const combinationCount = useMemo(() => countDesignCombinations(attributes), [attributes]);

  const updateSettings = useCallback((patch: Partial<AIDesignerSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const clearError = useCallback(() => setError(''), []);

  // Keep the selected mode valid for the user's plan once restrictions load.
  useEffect(() => {
    const { hasInitialLoad, availableModes, canUseMode } = modeRestrictions;
    if (hasInitialLoad && !canUseMode(mode)) {
      const availableMode = availableModes.find((option) => !option.disabled);
      if (availableMode) setMode(availableMode.value);
    }
  }, [modeRestrictions, mode]);

  // Estimation effect
  useEffect(() => {
    if (!currentUser?.uid || !clothImage) return;

    estimateCreditCost({
      clothImage,
      clothImage2: null,
      clothType: settings.clothType,
      clothAttributes: attributes,
      designTheme: settings.designTheme,
      colorPalette: settings.colorPalette,
      style: settings.style,
      aspectRatio: settings.aspectRatio,
      prompt: '',
      numImages: settings.variations,
      mode: mode.toLowerCase(),
      userId: currentUser.uid,
    });
  }, [
    currentUser?.uid,
    clothImage,
    settings.clothType,
    settings.designTheme,
    settings.colorPalette,
    settings.style,
    settings.aspectRatio,
    settings.variations,
    mode,
    attributes,
    estimateCreditCost,
  ]);

  const startTimer = useCallback(() => {
    setGenerationTime(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setGenerationTime((prev) => prev + 0.1);
    }, 100);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const addImage = useCallback(
    async (file: File) => {
      setUploading(true);
      setUploadError(null);
      clearError();
      uploadAbortRef.current?.abort();
      const controller = new AbortController();
      uploadAbortRef.current = controller;

      try {
        const url = await uploadClothImage(file, currentUser?.uid || 'anonymous', controller.signal);
        setClothImage(url);
        setIsExample(false);
        setGeneratedImages([]);
      } catch (err) {
        if (!controller.signal.aborted) {
          setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
        }
      } finally {
        if (!controller.signal.aborted) setUploading(false);
      }
    },
    [clearError, currentUser?.uid]
  );

  const removeImage = useCallback(() => {
    setClothImage('');
    setUploadError(null);
    setGeneratedImages([]);
    setIsExample(false);
  }, []);

  const loadExample = useCallback((preset: AIDesignerExamplePreset) => {
    setClothImage(preset.clothPreview);
    setGeneratedImages([]);
    setIsExample(true);
    setUploadError(null);
    clearError();
  }, [clearError]);

  const clearAll = useCallback(() => {
    setClothImage('');
    setSettings({ ...DEFAULT_SETTINGS });
    setAttributes({});
    setMode('performance');
    setGeneratedImages([]);
    setIsExample(false);
    setError('');
    setUploadError(null);
  }, []);

  const generate = useCallback(async (): Promise<GenerateOutcome> => {
    if (!clothImage) return 'no-image';
    if (!currentUser) return 'not-authenticated';
    if (!creditEstimation.canAfford) return 'insufficient-credits';

    generationAbortRef.current?.abort();
    const controller = new AbortController();
    generationAbortRef.current = controller;

    setIsProcessing(true);
    setGeneratedImages([]);
    setIsExample(false);
    setError('');
    startTimer();

    try {
      const result = await generateDesign(
        {
          clothImage,
          clothImage2: null,
          gender: settings.gender,
          clothType: settings.clothType,
          clothAttributes: attributesToPayload(attributes),
          designTheme: settings.designTheme,
          colorPalette: settings.colorPalette,
          style: settings.style,
          aspectRatio: settings.aspectRatio,
          prompt: '',
          numImages: settings.variations,
          mode: mode.toLowerCase(),
          userId: currentUser.uid,
          outputFormat: 'jpeg',
          enableSafetyChecker: true,
          type: 'ai-designer',
        },
        controller.signal
      );

      if (result.success && Array.isArray(result.imageUrls) && result.imageUrls.length > 0) {
        setGeneratedImages(result.imageUrls);
        await refreshUserCredits();
        return 'success';
      }

      setError(result.message || 'Failed to generate design. Please try again.');
      return 'error';
    } catch (err) {
      if (!controller.signal.aborted) {
        setError('Failed to generate design. Please try again.');
      }
      return 'error';
    } finally {
      if (!controller.signal.aborted) {
        stopTimer();
        setIsProcessing(false);
      }
    }
  }, [
    clothImage,
    currentUser,
    creditEstimation.canAfford,
    settings,
    attributes,
    mode,
    startTimer,
    stopTimer,
    refreshUserCredits,
  ]);

  // Cleanup timers and in-flight requests on unmount.
  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      generationAbortRef.current?.abort();
      uploadAbortRef.current?.abort();
    },
    []
  );

  return {
    clothImage,
    hasPrimaryImage,
    uploading,
    uploadError,
    addImage,
    removeImage,
    loadExample,
    clearAll,

    settings,
    updateSettings,

    attributes,
    setAttributes,
    combinationCount,

    mode,
    setMode,
    modeRestrictions,

    creditEstimation,
    userCredits,

    generatedImages,
    isExample,
    isProcessing,
    generationTime,
    error,
    generate,

    examplePresets: aiDesignerExamplePresets,
  };
};
